import { APIScopeData } from '@ardoq/api-types';
import { DropdownOption, DropdownOptionType } from '@ardoq/dropdown-menu';
import {
  actionCreator,
  dispatchAction,
  reducedStream,
  streamReducer,
  reducer,
} from '@ardoq/rxbeach';
import { scopeDataOperations } from '@ardoq/scope-data';
import { Observable } from 'rxjs';
import { localeCompare, getCurrentLocale } from '@ardoq/locale';

type ScopeDataWithWorkspaceOptionsStateInternal = {
  scopeDataInput: APIScopeData;
  scopeData: APIScopeData;
  disabledWorkspaceIds: Set<string>;
  workspaceOptions: DropdownOption[];
};

const workspaceOptionToggled = actionCreator<string>(
  '[WorkspaceOptions] SelectComponentsWorkspaceOptionToggled'
);

export type ScopeDataWithWorkspaceOptionsState = {
  scopeData: APIScopeData;
  workspaceOptions: DropdownOption[];
};

export const getScopeDataWithWorkspaceOptionsStream = (
  scopeData$: Observable<APIScopeData>,
  actionNamespace: string
): Observable<ScopeDataWithWorkspaceOptionsState> => {
  const toWorkspaceOptions = (
    state: ScopeDataWithWorkspaceOptionsStateInternal
  ) => {
    const newWorkspaceOptions: DropdownOption[] =
      state.scopeDataInput.workspaces
        .map(({ name, _id: id }) => ({
          label: name,
          type: DropdownOptionType.OPTION,
          isActive: !state.disabledWorkspaceIds.has(id),
          onClick: () => {
            dispatchAction(workspaceOptionToggled(id), actionNamespace);
          },
        }))
        .sort((a, b) => localeCompare(a.label, b.label, getCurrentLocale()));
    return { ...state, workspaceOptions: newWorkspaceOptions };
  };

  const scopeDataReducer = (
    _: ScopeDataWithWorkspaceOptionsStateInternal,
    newScopeData: APIScopeData
  ): ScopeDataWithWorkspaceOptionsStateInternal => {
    return toWorkspaceOptions({
      scopeDataInput: newScopeData,
      scopeData: newScopeData,
      disabledWorkspaceIds: new Set(),
      workspaceOptions: [],
    });
  };

  const workspaceOptionToggledReducer = (
    state: ScopeDataWithWorkspaceOptionsStateInternal,
    toggledWorkspaceId: string
  ): ScopeDataWithWorkspaceOptionsStateInternal => {
    const newDisabledWorkspaceIds = new Set(state.disabledWorkspaceIds);

    if (newDisabledWorkspaceIds.has(toggledWorkspaceId)) {
      newDisabledWorkspaceIds.delete(toggledWorkspaceId);
    } else {
      newDisabledWorkspaceIds.add(toggledWorkspaceId);
    }

    const filteredWorkspaces = state.scopeDataInput.workspaces.filter(
      ws => !newDisabledWorkspaceIds.has(ws._id)
    );
    const filteredComponents = state.scopeDataInput.components.filter(
      comp => !newDisabledWorkspaceIds.has(comp.rootWorkspace)
    );

    const scopeData: APIScopeData = {
      ...state.scopeDataInput,
      workspaces: filteredWorkspaces,
      components: filteredComponents,
    };

    const newState: ScopeDataWithWorkspaceOptionsStateInternal = {
      ...state,
      scopeData,
      disabledWorkspaceIds: newDisabledWorkspaceIds,
      workspaceOptions: [],
    };

    return toWorkspaceOptions(newState);
  };

  const scopeDataWithWorkspaceOptions$: Observable<ScopeDataWithWorkspaceOptionsStateInternal> =
    reducedStream(
      'selectComponentsWorkspaceOptions$',
      {
        scopeDataInput: scopeDataOperations.getEmpty(),
        scopeData: scopeDataOperations.getEmpty(),
        disabledWorkspaceIds: new Set(),
        workspaceOptions: [],
      },
      [
        streamReducer(scopeData$, scopeDataReducer),
        reducer(workspaceOptionToggled, workspaceOptionToggledReducer),
      ],
      { namespace: actionNamespace }
    );
  return scopeDataWithWorkspaceOptions$;
};
